import type { FetchFnReturnType } from "../../common-deprecated/utils/backendFetch";
import type { TradeInAemSettingsType } from "../redux/reducers/TradeInAemSettingsReducer";
import type { TradeInAemDispatchType, TradeInAemStateType } from "../redux/store";
import { initTradeInAemSettings } from "../redux/actions/TradeInAemSettingsActions";
import type { FetchTradeInAemModelSelectInfoType } from "../server/fetchTradeInAemModelSelectInfo";
import {
    tradeInAemModelSelectSetModelId,
    tradeInAemModelSelectSetModelList,
} from "../redux/actions/TradeInAemModelSelectActions";
import type { TradeInAemContactFormReducerType } from "../redux/reducers/TradeInAemContactFormReducer";
import { initContactForm } from "../redux/actions/TradeInAemContactFormActions";
import { COMPONENT_TRADEIN_AEM_PDF } from "../../shared-logic/server/components";
import { tradeInAemVehicleSetVehicle } from "../redux/actions/TradeInAemVehicleActions";
import { tradeInAemSetQuote } from "../redux/actions/TradeInAemVehicleValueActions";
import type { TradeInAemVehicleExtraInfoType } from "../utils/types";
import type { DealerResultType } from "../../common-deprecated/utils/dealerConstants";
import Debug from "../../common-deprecated/Debug";
import { tradeInAemDealerSetDealer } from "../redux/actions/TradeInAemDealerActions";

// ----------------------------------------------------------------------
// Fetch types
// ----------------------------------------------------------------------

export const FETCH_TRADE_IN_AEM_SETTINGS = "fetchTradeInAemSettings";
type FetchTradeInAemSettingsType = {
    fetchType: typeof FETCH_TRADE_IN_AEM_SETTINGS;
    result?: FetchFnReturnType<TradeInAemSettingsType>;
};
const fetchTradeInAemSettings = (): FetchTradeInAemSettingsType => ({
    fetchType: FETCH_TRADE_IN_AEM_SETTINGS,
});

export const FETCH_TRADE_IN_AEM_CONTACT_FORM = "fetchTradeInAemContactForm";
type FetchTradeInAemContactForm = {
    fetchType: typeof FETCH_TRADE_IN_AEM_CONTACT_FORM;
    result?: FetchFnReturnType<TradeInAemContactFormReducerType>;
};
export const fetchTradeInAemContactForm = (): FetchTradeInAemContactForm => ({
    fetchType: FETCH_TRADE_IN_AEM_CONTACT_FORM,
});

export const FETCH_TRADE_IN_AEM_MODEL_SELECT_INFO = "fetchTradeInAemModelSelectInfo";
type FetchTradeInAemNewCarInfo = {
    fetchType: typeof FETCH_TRADE_IN_AEM_MODEL_SELECT_INFO;
    result?: FetchTradeInAemModelSelectInfoType | null;
};
export const fetchTradeInAemNewCarInfo = (): FetchTradeInAemNewCarInfo => ({
    fetchType: FETCH_TRADE_IN_AEM_MODEL_SELECT_INFO,
});

export const FETCH_TRADE_IN_QUOTE_ID = "fetchTradeInQuoteId";
type FetchTradeInQuoteId = {
    fetchType: typeof FETCH_TRADE_IN_QUOTE_ID;
    result?: any;
};
export const fetchTradeInQuoteId = (): FetchTradeInQuoteId => ({
    fetchType: FETCH_TRADE_IN_QUOTE_ID,
});

export const FETCH_TRADE_IN_DEALER = "fetchTradeInDealer";
type FetchTradeInDealerType = {
    fetchType: typeof FETCH_TRADE_IN_DEALER;
    dealerId: string;
    result?: FetchFnReturnType<DealerResultType | null> | null;
};
export const fetchTradeInDealer = (dealerId: string): FetchTradeInDealerType => ({
    fetchType: FETCH_TRADE_IN_DEALER,
    dealerId,
});

export type TradeInAemFetchTypes =
    | FetchTradeInAemSettingsType
    | FetchTradeInAemContactForm
    | FetchTradeInAemNewCarInfo
    | FetchTradeInQuoteId
    | FetchTradeInDealerType;

// ----------------------------------------------------------------------
// Fetch getter
// ----------------------------------------------------------------------
export const getTradeInAemComponentFetches = (state: TradeInAemStateType): TradeInAemFetchTypes[] => {
    const fetches: TradeInAemFetchTypes[] = [];
    const {
        tradeInAemSettings,
        tradeInAemModelSelect,
        tradeInAemContact,
        tradeInAemVehicle,
        tradeInAemDealer,
        commonSettings,
    } = state;
    const isPDF = commonSettings.component === COMPONENT_TRADEIN_AEM_PDF;

    if (!tradeInAemSettings.initialized) {
        fetches.push(fetchTradeInAemSettings());
    }
    if (!tradeInAemContact.initialized) {
        fetches.push(fetchTradeInAemContactForm());
    }

    if (isPDF) {
        if (!tradeInAemVehicle.vehicle) {
            fetches.push(fetchTradeInQuoteId());
        }

        if (tradeInAemVehicle.vehicle && !tradeInAemDealer.dealer) {
            const dealerId = (tradeInAemVehicle.vehicle?.tmeExtraInfo as TradeInAemVehicleExtraInfoType)?.dealerId;
            if (dealerId) fetches.push(fetchTradeInDealer(dealerId));
        }
    }

    // The trade-in new car info is only fetched when there is a modelSelect step
    // As it is not relevant in the first steps this info is also only fetched clientside after init
    // The 'tradeInAemModelSelect.enabled' is set to true in the 'postFetch' to enforce this logic is only executed clientside during 'useDataFetch'
    // For the PDF the 'tradeInAemModelSelect.enabled' is set to true in the 'prefetch' so it's always included
    if (!tradeInAemModelSelect.initialized && tradeInAemModelSelect.enabled) {
        fetches.push(fetchTradeInAemNewCarInfo());
    }

    return fetches;
};

// ----------------------------------------------------------------------
// Dispatcher
// ----------------------------------------------------------------------
export const tradeInAemFetchDispatcher = (
    data: Required<TradeInAemFetchTypes>,
    dispatch: TradeInAemDispatchType,
): void => {
    switch (data.fetchType) {
        case FETCH_TRADE_IN_AEM_SETTINGS: {
            if (data.result.fetchResult) {
                dispatch(initTradeInAemSettings(data.result.fetchResult));
            }
            break;
        }
        case FETCH_TRADE_IN_AEM_CONTACT_FORM: {
            if (data.result.fetchResult) {
                dispatch(initContactForm(data.result.fetchResult));
            }
            break;
        }
        case FETCH_TRADE_IN_AEM_MODEL_SELECT_INFO: {
            const { result } = data;
            if (result?.fetchResult.modelList) {
                dispatch(tradeInAemModelSelectSetModelList(result.fetchResult.modelList));
            }
            break;
        }
        case FETCH_TRADE_IN_QUOTE_ID: {
            const { result } = data;

            // Set the retrieved result as both vehicle result as valuation
            dispatch(tradeInAemVehicleSetVehicle(result));
            dispatch(tradeInAemSetQuote(result));

            // Set the selectedModelId which was added to the 'tmeExtraInfo'
            const selectedModelId = (result?.tmeExtraInfo as TradeInAemVehicleExtraInfoType)?.selectedModelId;
            if (selectedModelId) dispatch(tradeInAemModelSelectSetModelId(selectedModelId));
            break;
        }

        case FETCH_TRADE_IN_DEALER: {
            if (data.result?.fetchResult) {
                const { fetchResult } = data.result;
                dispatch(tradeInAemDealerSetDealer(fetchResult));
            } else {
                Debug.warn(`Cannot find dealer with id ${data.dealerId}`);
            }

            break;
        }
    }
};
